import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { CommonModule } from '@angular/common';
import { Subject, Observable } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';

@Component({
  selector: 'app-autocomplete-select',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    NgxSpinnerModule,
  ],
  templateUrl: './autocomplete-select.component.html',
  styleUrls: ['./autocomplete-select.component.scss']
})
export class AutocompleteSelectComponent implements OnInit, OnDestroy {
  @Input() label: string = 'Search';
  @Input() placeholder: string = 'Type at least 3 characters...';
  @Input() options: any[] = [];
  @Input() displayProperty: string = ''; // Property to display if options are objects
  @Input() disabled: boolean = false;
  @Input() isFullWidth: boolean = false; // CSS class to apply to mat-form-field
  @Input() searchFunction?: (searchTerm: string) => Observable<any[]>; // Optional server-side search function
  @Input() maxResults: number = 100; // Cap on shown results; 0 = show all
  @Input() panelClass: string = ''; // Forwarded to mat-autocomplete's panelClass

  @Output() optionSelected = new EventEmitter<any>();

  public searchControl: FormControl = new FormControl('');
  public filteredOptions: any[] = [];
  public hasMoreResults: boolean = false;
  public totalFilteredCount: number = 0;
  public isLoading: boolean = false;
  public isPanelOpen: boolean = false;

  private destroy$ = new Subject<void>();

  constructor(private spinnerService: NgxSpinnerService) {}

  ngOnInit(): void {
    // Flip isLoading immediately on input (before debounce) so consumers can hide
    // "no results" affordances during the in-flight period.
    this.searchControl.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(searchTerm => {
      const value = (searchTerm ?? '').toString();
      this.isLoading = value.length >= 3;
    });

    this.searchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(searchTerm => {
      if (this.searchFunction) {
        this.performServerSearch(searchTerm);
      } else {
        this.filterOptions(searchTerm);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public getDisplayText(option: any): string {
    if (typeof option === 'string') {
      return option;
    }
    if (this.displayProperty && option[this.displayProperty]) {
      return option[this.displayProperty];
    }
    return option.toString();
  }

  public onOptionSelected(option: any): void {
    this.optionSelected.emit(option);
    this.searchControl.setValue(this.getDisplayText(option)); // Set the input to show the selected value
  }

  private filterOptions(searchTerm: string): void {
    if (!searchTerm || searchTerm.length < 3) {
      this.filteredOptions = [];
      this.hasMoreResults = false;
      this.totalFilteredCount = 0;
      this.isLoading = false;
      return;
    }

    const filterValue = searchTerm.toLowerCase();
    const allFilteredOptions = this.options
      .filter(option => {
        const displayText = this.getDisplayText(option).toLowerCase();
        return displayText.includes(filterValue);
      });

    this.totalFilteredCount = allFilteredOptions.length;
    if (this.maxResults > 0) {
      this.hasMoreResults = allFilteredOptions.length > this.maxResults;
      this.filteredOptions = allFilteredOptions.slice(0, this.maxResults);
    } else {
      this.hasMoreResults = false;
      this.filteredOptions = allFilteredOptions;
    }
    this.isLoading = false;
  }

  private performServerSearch(searchTerm: string): void {
    if (!searchTerm || searchTerm.length < 3) {
      this.filteredOptions = [];
      this.hasMoreResults = false;
      this.totalFilteredCount = 0;
      this.isLoading = false;
      this.spinnerService.hide();
      return;
    }

    if (!this.searchFunction) {
      return;
    }

    this.spinnerService.show();
    this.searchFunction(searchTerm).subscribe({
      next: (results) => {
        this.totalFilteredCount = results.length;
        if (this.maxResults > 0) {
          this.filteredOptions = results.slice(0, this.maxResults);
          this.hasMoreResults = results.length > this.maxResults;
        } else {
          this.filteredOptions = results;
          this.hasMoreResults = false;
        }
        this.isLoading = false;
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Search error:', error);
        this.filteredOptions = [];
        this.isLoading = false;
        this.spinnerService.hide();
      }
    });
  }
}
